import React, { useState, useRef, useEffect, useCallback } from 'react';
import { cancelVideoProcessing } from '../../../services/api';
import { buildApiUrl, API_ENDPOINTS } from '../../../config/api';
import { getCsrfHeaders } from '../../../utils/csrf';
import { useAuth } from '../../../context/AuthContext';
import { authFetch } from '../../../utils/authFetch';
import './loading.css';
import DownloadStatus from '../../common/DownloadStatus';
// import PremiumFeatures from '../../feedback/PremiumFeatures';
import FeedbackSection from '../../feedback/FeedbackSection';
// import ComingSoon from '../../feedback/ComingSoon';
import RegistrationCTA from '../../common/RegistrationCTA/RegistrationCTA';

// Add status checking function
const checkVideoStatus = async (sessionId) => {
  try {
    const response = await authFetch(buildApiUrl(API_ENDPOINTS.STATUS(sessionId)));
    if (response.ok) {
      return await response.json();
    }
    throw new Error('Failed to check status');
  } catch (error) {
    console.error('Status check error:', error);
    return null;
  }
};


// Maximum file size in bytes (200MB)
const MAX_VIDEO_SIZE = 200 * 1024 * 1024;

// Supported video formats
const SUPPORTED_FORMATS = ['.mp4', '.avi', '.mov', '.mkv', '.webm'];

// File name validation regex (based on backend requirements)
const VALID_FILENAME_PATTERN = /^[a-zA-Z0-9_\-.\s[\]()\s,']+$/;

const MAX_VIDEO_DURATION_SECONDS = 600; // 10 minutes

const UploadForm = () => {
  const { getAuthHeaders } = useAuth();
  const [video, setVideo] = useState(null);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const [fileText, setFileText] = useState("");
  const [downloadStatus, setDownloadStatus] = useState(null);
  const [downloadMessage, setDownloadMessage] = useState("");
  const [error, setError] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [usageInfo, setUsageInfo] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const pollTimeoutRef = useRef(null);

  // Fetch usage info on component mount
  useEffect(() => {
    const fetchUsageInfo = async () => {
      try {
        const response = await authFetch(buildApiUrl(API_ENDPOINTS.USAGE()), {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const usage = await response.json();
          setUsageInfo(usage);
        }
      } catch (error) {
        console.error('Usage check error:', error);
      }
    };
    fetchUsageInfo();
  }, [getAuthHeaders]);

  // Add status polling function inside component to access state
  const pollVideoStatus = useCallback((sessionId) => {
    // Clear any existing polling
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
    }
    
    pollIntervalRef.current = setInterval(async () => {
      const status = await checkVideoStatus(sessionId);
      if (status) {
        setProcessingStatus(status.status);
        
        // Update localStorage
        const savedState = localStorage.getItem('extractionState');
        if (savedState) {
          const state = JSON.parse(savedState);
          state.status = status.status;
          localStorage.setItem('extractionState', JSON.stringify(state));
        }
        
        if (status.status === 'completed') {
          setIsProcessing(false);
          setUploadCompleted(true);
          setMessage({
            text: 'Video processing completed successfully! You can now download your scenes.',
            type: 'success'
          });
          // Clear both interval and timeout
          clearInterval(pollIntervalRef.current);
          clearTimeout(pollTimeoutRef.current);
          pollIntervalRef.current = null;
          pollTimeoutRef.current = null;
        } else if (status.status === 'failed') {
          setIsProcessing(false);
          setMessage({
            text: `Processing failed: ${status.error_message || 'Unknown error'}`,
            type: 'error'
          });
          clearInterval(pollIntervalRef.current);
          clearTimeout(pollTimeoutRef.current);
          pollIntervalRef.current = null;
          pollTimeoutRef.current = null;
        } else if (status.status === 'cancelled') {
          setIsProcessing(false);
          setMessage({
            text: 'Video processing was cancelled',
            type: 'info'
          });
          clearInterval(pollIntervalRef.current);
          clearTimeout(pollTimeoutRef.current);
          pollIntervalRef.current = null;
          pollTimeoutRef.current = null;
        }
      }
    }, 2000); // Check every 2 seconds
    
    // Cleanup after 10 minutes (600 seconds)
    pollTimeoutRef.current = setTimeout(() => {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
      pollTimeoutRef.current = null;
      setIsProcessing(false);
      setMessage({
        text: 'Processing timeout. Please try again.',
        type: 'error'
      });
    }, 600000);
  }, []);

  // Check for existing job on mount
  useEffect(() => {
    const savedState = localStorage.getItem('extractionState');
    if (savedState) {
      const state = JSON.parse(savedState);
      setSessionId(state.sessionId);
      setProcessingStatus(state.status);
      
      if (state.filename) {
        setVideo({ name: state.filename });
        setFileText(state.filename);
      }
      
      if (state.status === 'pending' || state.status === 'processing') {
        setIsProcessing(true);
        pollVideoStatus(state.sessionId);
      } else if (state.status === 'completed') {
        setUploadCompleted(true);
        setIsProcessing(false);
        setMessage({
          text: 'Video processing completed! You can download your scenes.',
          type: 'success'
        });
      } else if (state.status === 'failed') {
        setIsProcessing(false);
        setMessage({
          text: 'Video processing failed.',
          type: 'error'
        });
      }
    }
  }, [pollVideoStatus]);

  // Handle cancellation of video processing or upload
  const handleCancelProcessing = async () => {
    // If we're in the middle of uploading (no sessionId yet), cancel the upload
    if (isLoading && !sessionId) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      setIsLoading(false);
      setMessage({
        text: 'Upload cancelled successfully',
        type: 'info'
      });
      return;
    }

    // If we have a sessionId, cancel the processing
    if (!sessionId) {
      setMessage({
        text: 'No active processing to cancel',
        type: 'error'
      });
      return;
    }

    try {
      await cancelVideoProcessing(sessionId);
      setIsProcessing(false);
      setIsLoading(false);
      setMessage({
        text: 'Video processing cancelled successfully',
        type: 'success'
      });
      
      // Update localStorage
      const savedState = localStorage.getItem('extractionState');
      if (savedState) {
        const state = JSON.parse(savedState);
        state.status = 'cancelled';
        localStorage.setItem('extractionState', JSON.stringify(state));
      }
    } catch (error) {
      console.error('Error cancelling processing:', error);
      setMessage({
        text: 'Failed to cancel processing. Please try again.',
        type: 'error'
      });
    }
  };

  const handleUpload = async () => {
    if (!video) return;

    setIsLoading(true);
    setMessage("");
    setUploadProgress(0);

    // Create new AbortController for this upload
    abortControllerRef.current = new AbortController();

    try {
      // Step 1: Initiate upload - get presigned URL
      setMessage({
        text: 'Preparing upload...',
        type: 'info'
      });

      const initiateResponse = await authFetch(buildApiUrl('/upload/initiate/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getCsrfHeaders(),
        },
        body: JSON.stringify({
          filename: video.name,
          content_type: video.type || 'video/mp4',
          file_size: video.size
        }),
        signal: abortControllerRef.current.signal
      });

      if (!initiateResponse.ok) {
        const errorData = await initiateResponse.json();
        
        // Check if error is due to unverified email
        if (errorData.error_type === 'email_not_verified' || initiateResponse.status === 403) {
          throw new Error('⚠️ Please verify your email before uploading videos. Check your inbox for the verification link.');
        }
        
        throw new Error(errorData.error || 'Failed to initiate upload');
      }

      const initiateData = await initiateResponse.json();
      const { upload_url, object_key, session_id } = initiateData;

      // Save session immediately
      setSessionId(session_id);
      localStorage.setItem('extractionState', JSON.stringify({
        sessionId: session_id,
        status: 'uploading',
        filename: video.name
      }));

      // Step 2: Upload directly to R2 with progress tracking
      setMessage({
        text: 'Uploading video...',
        type: 'info'
      });

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // Track upload progress
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(percentComplete);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload aborted'));
        });

        // Handle abort from AbortController
        if (abortControllerRef.current) {
          abortControllerRef.current.signal.addEventListener('abort', () => {
            xhr.abort();
          });
        }

        xhr.open('PUT', upload_url);
        xhr.setRequestHeader('Content-Type', video.type || 'video/mp4');
        xhr.send(video);
      });

      setUploadProgress(100);

      // Step 3: Complete upload - notify backend
      setMessage({
        text: 'Finalizing upload...',
        type: 'info'
      });

      const completeResponse = await authFetch(buildApiUrl('/upload/complete/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getCsrfHeaders(),
        },
        body: JSON.stringify({
          session_id: session_id,
          object_key: object_key
        }),
        signal: abortControllerRef.current.signal
      });

      if (!completeResponse.ok) {
        const errorData = await completeResponse.json();
        throw new Error(errorData.error || 'Failed to complete upload');
      }

      const completeData = await completeResponse.json();

      // Update state for processing
      setIsProcessing(true);
      setProcessingStatus('pending');

      // Update localStorage
      localStorage.setItem('extractionState', JSON.stringify({
        sessionId: session_id,
        status: 'pending',
        filename: video.name
      }));

      // Start polling for status
      pollVideoStatus(session_id);

      setMessage({
        text: 'Video uploaded successfully! Processing has started.',
        type: 'success'
      });

      // Update usage info
      if (completeData.usage_info) {
        setUsageInfo(prev => ({
          ...prev,
          ...completeData.usage_info
        }));
      }

      // Refresh usage info
      try {
        const usageResponse = await authFetch(buildApiUrl(API_ENDPOINTS.USAGE()), {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (usageResponse.ok) {
          const updatedUsage = await usageResponse.json();
          setUsageInfo(updatedUsage);
        }
      } catch (error) {
        console.error('Error refreshing usage info:', error);
      }
    } catch (error) {
      // Check if the error is due to abort
      if (error.name === 'AbortError' || error.message === 'Upload aborted' || error.message?.includes('aborted')) {
        setMessage({
          text: 'Upload cancelled',
          type: 'info'
        });
        return;
      }

      console.error('Upload error:', error);
      setMessage({
        text: error.message || 'Error uploading video.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
      abortControllerRef.current = null;
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setError(''); // Clear previous errors

    if (!file) {
      setVideo(null);
      setFileText("No file selected");
      setMessage(null);
      return;
    }

    // Validate file size
    if (file.size > MAX_VIDEO_SIZE) {
      setVideo(null);
      setFileText("File too large");
      setMessage({
        text: "File is too large. Maximum size is 200MB.",
        type: 'error'
      });
      return;
    }

    // Validate file format
    const ext = file.name.toLowerCase().split('.').pop();
    if (!SUPPORTED_FORMATS.includes(`.${ext}`)) {
      setVideo(null);
      setFileText("Unsupported format");
      setMessage({
        text: `Unsupported video format. Supported formats: ${SUPPORTED_FORMATS.map(ext => ext.slice(1).toUpperCase()).join(', ')}`,
        type: 'error'
      });
      return;
    }

    // Validate file name
    if (!VALID_FILENAME_PATTERN.test(file.name)) {
      setVideo(null);
      setFileText("Unsupported Characters");
      setMessage({
        text: "File name contains unsupported characters. Allowed characters: letters, numbers, spaces, underscores, hyphens, dots, square brackets, parentheses, commas, apostrophes.",
        type: 'error'
      });
      return;
    }

    // Validate video duration before proceeding
    const url = URL.createObjectURL(file);
    const videoElement = document.createElement('video');
    videoElement.preload = 'metadata';

    videoElement.onloadedmetadata = () => {
      window.URL.revokeObjectURL(url);
      if (videoElement.duration > MAX_VIDEO_DURATION_SECONDS) {
//         setError('Video is too long. Maximum allowed duration is 10 minutes.');
        setFileText("Too long video.");
          setMessage({
            text: "Your video is too long. Maximum allowed duration is 10 minutes.",
            type: 'error'
          });
        // Optionally clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      setVideo(file);
      setFileText(file.name);
      setMessage(null);
    };

    videoElement.onerror = () => {
      setError('Could not read video file for duration check.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    videoElement.src = url;
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Prevent drag over during processing or when upload is completed
    if (isLoading || isProcessing || uploadCompleted) return;
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    // Prevent drop during processing or when upload is completed
    if (isLoading || isProcessing || uploadCompleted) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      // Use the same validation logic as file input
      handleFileFromDrop(file);
    }
  };

  const handleFileFromDrop = (file) => {
    setError(''); // Clear previous errors

    // Validate file size
    if (file.size > MAX_VIDEO_SIZE) {
      setVideo(null);
      setFileText("File too large");
      setMessage({
        text: "File is too large. Maximum size is 200MB.",
        type: 'error'
      });
      return;
    }

    // Validate file format
    const ext = file.name.toLowerCase().split('.').pop();
    if (!SUPPORTED_FORMATS.includes(`.${ext}`)) {
      setVideo(null);
      setFileText("Unsupported format");
      setMessage({
        text: `Unsupported video format. Supported formats: ${SUPPORTED_FORMATS.map(ext => ext.slice(1).toUpperCase()).join(', ')}`,
        type: 'error'
      });
      return;
    }

    // Validate file name
    if (!VALID_FILENAME_PATTERN.test(file.name)) {
      setVideo(null);
      setFileText("Unsupported Characters");
      setMessage({
        text: "File name contains unsupported characters. Allowed characters: letters, numbers, spaces, underscores, hyphens, dots, square brackets, parentheses, commas, apostrophes.",
        type: 'error'
      });
      return;
    }

    // Validate video duration before proceeding
    const url = URL.createObjectURL(file);
    const videoElement = document.createElement('video');
    videoElement.preload = 'metadata';

    videoElement.onloadedmetadata = () => {
      window.URL.revokeObjectURL(url);
      if (videoElement.duration > MAX_VIDEO_DURATION_SECONDS) {
        setFileText("Too long video.");
        setMessage({
          text: "Your video is too long. Maximum allowed duration is 10 minutes.",
          type: 'error'
        });
        return;
      }
      setVideo(file);
      setFileText(file.name);
      setMessage(null);
    };

    videoElement.onerror = () => {
      setError('Could not read video file for duration check.');
    };

    videoElement.src = url;
  };

  const resetForm = () => {
    // Cancel any ongoing upload
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Clear localStorage
    localStorage.removeItem('extractionState');
    
    setVideo(null);
    setMessage(null);
    setUploadCompleted(false);
    setIsLoading(false);
    setIsProcessing(false);
    setProcessingStatus(null);
    setSessionId(null);
    setDownloadStatus(null);
    setDownloadMessage(null);
    setFileText("No file selected");
    setIsDragOver(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadScenes = async () => {
    if (!sessionId || !video) {
      setDownloadStatus('error');
      setDownloadMessage('Video not available for download');
      return;
    }

    try {
      // Fetch presigned URL from backend
      const downloadEndpoint = `${buildApiUrl(API_ENDPOINTS.DOWNLOAD(sessionId))}?original_name=${encodeURIComponent(video.name)}`;
      
      const response = await authFetch(downloadEndpoint);
      
      if (!response.ok) {
        throw new Error('Failed to get download URL');
      }
      
      const data = await response.json();
      
      // Open presigned URL directly in browser
      window.location.href = data.download_url;
      
      setDownloadStatus('success');
      setDownloadMessage('Download started!');
      
      setTimeout(() => {
        setDownloadStatus(null);
        setDownloadMessage("");
      }, 3000);

    } catch (error) {
      console.error('Download error:', error);
      setDownloadStatus('error');
      setDownloadMessage(error.message || 'Error occurred during download');
    }
  };



  return (
    <div className="w-full">
      <div className="max-w-4xl mx-auto bg-slate-700 bg-opacity-30 rounded-xl p-8">
        <div className="space-y-6">
          <div className="flex flex-col items-center">
            <h2 className="text-2xl font-bold text-slate-100 mb-2">Shot Extraction</h2>
            <p className="text-slate-400 text-center mb-6 text-sm">
              Upload your video to automatically detect and extract individual shots
            </p>
            <div className="flex flex-col items-center gap-6">
              {/* Drag and Drop Zone */}
              <div 
                className={`w-full max-w-md border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  (isLoading || isProcessing || uploadCompleted)
                    ? 'border-slate-600 bg-slate-900 opacity-50 cursor-not-allowed'
                    : isDragOver 
                      ? 'border-blue-400 bg-blue-500 bg-opacity-5'
                      : 'border-slate-600 hover:border-slate-500'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="fileInput"
                  onChange={handleFileChange}
                  accept=".mp4,.avi,.mov,.mkv,.webm"
                  className="hidden"
                  ref={fileInputRef}
                  disabled={isLoading || isProcessing || uploadCompleted}
                />
                
                <div className="flex flex-col items-center space-y-4">
                  
                  <div className="space-y-2">
                    <label
                      htmlFor="fileInput"
                      className={`inline-block py-2 px-4 rounded-lg transition-colors ${
                        (isLoading || isProcessing || uploadCompleted)
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                          : 'bg-blue-500 hover:bg-blue-600 text-white cursor-pointer'
                      } font-semibold`}
                    >
                      Browse Files
                    </label>
                    <p className="text-sm text-gray-500">
                      or
                    </p>
                    <p className="text-lg text-gray-300">
                      {isDragOver ? 'Drop your video here' : 'Drag your video here'}
                    </p>
                  </div>
                  
                  <p className="text-xs text-gray-500">
                    Supported: MP4, AVI, MOV, MKV, WEBM (max 200MB, 10min)
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col items-center">
                <p className={`text-green-500 text-base ${fileText === "No file selected" ? 'text-gray-400' : ''}`}>
                  {fileText}
                </p>
                {error && <div style={{ color: 'red', marginTop: '8px' }}>{error}</div>}
                
                {/* Usage Info Display */}
                {usageInfo && (
                  <div className="mt-3 text-sm text-gray-400 text-center">
                    {usageInfo.limits?.total_limit ? (
                      <>
                        <div className="mb-1">
                          Used {usageInfo.usage?.total_videos || 0}/{usageInfo.limits?.total_limit} total
                        </div>
                        {usageInfo.remaining?.total_remaining <= 1 && usageInfo.remaining?.total_remaining > 0 && (
                          <div className="text-yellow-400 text-xs">
                            ⚠️ Only {usageInfo.remaining.total_remaining} video left
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="mb-1">
                          Used {usageInfo.usage?.videos_today || 0}/{usageInfo.limits?.daily_limit || 20} past 24 hours
                        </div>
                        <div className="mb-1">
                          {usageInfo.usage?.videos_this_hour || 0}/{usageInfo.limits?.hourly_limit || 5} this hour
                        </div>
                        {usageInfo.remaining?.daily_remaining <= 3 && usageInfo.remaining?.daily_remaining > 0 && (
                          <div className="text-yellow-400 text-xs">
                            ⚠️ Only {usageInfo.remaining.daily_remaining} videos left today
                          </div>
                        )}
                        {usageInfo.remaining?.hourly_remaining <= 2 && usageInfo.remaining?.hourly_remaining > 0 && (
                          <div className="text-orange-400 text-xs">
                            ⚠️ Only {usageInfo.remaining.hourly_remaining} videos left this hour
                          </div>
                        )}
                      </>
                    )}
                    {!usageInfo.can_upload && (
                      <div className="text-red-400 text-xs">
                        ❌ {usageInfo.message}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Registration CTA for anonymous users */}
                <RegistrationCTA />
              </div>
            </div>
{/*             <div className="space-y-2"> */}
{/*               <p className="text-gray-400 text-sm text-center"> */}
{/*                 <span className="font-medium">File Limits:</span> */}
{/*                 <br/> */}
{/*                 Maximum size: 200MB */}
{/*                 <br/> */}
{/*                 Supported formats: MP4, AVI, MOV, MKV, WEBM */}
{/*               </p> */}
{/*             </div> */}

            {message && (
              <div className="mt-4">
                <div className={`p-4 rounded-lg ${message.type === 'error' ? 'bg-red-600' : 'bg-green-600'} text-white flex items-center justify-center`}>
                  {message.text}
                </div>
              </div>
            )}
          </div>

          {video && (
            <div className="flex flex-col items-center">
              {/* Show Process button only when video is selected and not loading/processing */}
              {!isLoading && !isProcessing && !uploadCompleted && (
                <button
                  onClick={handleUpload}
                  disabled={!video || (usageInfo && !usageInfo.can_upload)}
                  className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Process
                </button>
              )}

              {/* Show STOP button during upload and processing */}
              {(isLoading || isProcessing) && (
                <div className="flex flex-col items-center w-full">
                  <div className="loading-spinner"></div>
                  <p className="loading-text">
                    {isLoading ? "Uploading video..." : "Processing video..."}
                  </p>
                  
                  {/* Upload Progress Bar */}
                  {isLoading && uploadProgress > 0 && (
                    <div className="w-full max-w-md mt-4">
                      <div className="flex justify-between text-sm text-gray-400 mb-2">
                        <span>Upload Progress</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                        <div 
                          className="bg-blue-500 h-full transition-all duration-300 ease-out"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={handleCancelProcessing}
                    className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors mt-4"
                  >
                    {isLoading ? "CANCEL UPLOAD" : "STOP PROCESSING"}
                  </button>
                </div>
              )}

              {/* Show processing status during processing */}
              {isProcessing && (
                <div className="mt-4">
                  <div className="flex flex-col items-center">
                    <p className="text-gray-400 text-sm">Status: {processingStatus}</p>
                  </div>
                </div>
              )}

              {uploadCompleted && (
                <div className="mt-4">
                  {downloadStatus && (
                    <DownloadStatus
                      status={downloadStatus}
                      message={downloadMessage}
                      onClose={() => {
                        setDownloadStatus(null);
                        setDownloadMessage("");
                      }}
                    />
                  )}
                  <button
                    onClick={downloadScenes}
                    className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors w-full"
                  >
                    Download Scenes
                  </button>
                  <button
                    onClick={resetForm}
                    className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors w-full mt-4"
                  >
                    Upload New Video
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Feedback Section */}
          <div className="mt-6">
            <FeedbackSection />
          </div>

{/*            */}{/* Premium Features Section */}
{/*           <div className="mt-8"> */}
{/*             <PremiumFeatures /> */}
{/*           </div> */}

{/*            */}{/* Coming Soon Section */}
{/*           <div className="mt-6"> */}
{/*             <ComingSoon /> */}
{/*           </div> */}

        </div>
      </div>
    </div>
  );
};

export default UploadForm;
